import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Image as ImageIcon, Package, Pencil, Plus, RotateCcw, Star } from 'lucide-react';
import {
  Alert, AsyncBoundary, Badge, Button, Card, CardHeader, ConfirmDialog, Field,
  Input, Modal, Select, Textarea
} from '../ui';
import { BILLING_TYPES, adminService, formatMoney } from '../../services/agencyService';

// ⭐ Catálogo de productos (spec §3).
//
// Requisito explícito de la spec: "debe permitir agregar productos nuevos sin
// tener que modificar manualmente la interfaz". Por eso NO hay ninguna lista
// de productos escrita en este archivo — todo viene de
// agency_service_catalog. Los cuatro productos iniciales (Landing Básica
// $100, Captación $180, Comercial $280, Sistema Completo $450) están sembrados
// en backend/agency/schema.js, no acá.
//
// Los precios NO se tocan desde este archivo sin decisión explícita: el
// formulario los expone, pero cambiar un precio publicado es una decisión
// comercial que se registra en la bitácora, no un edit silencioso.

const EMPTY_PRODUCT = {
  name: '', slug: '', category: 'paginas-web', description: '', base_price: '',
  billing_type: 'unico', image_url: '', sort_order: 100, featured: false,
  active: true, benefits: '', deliverables: '', combos: '', promotional_price: '',
  promotion_label: '', landing_url: '', seo_title: '', seo_description: '', faq: ''
};

const CATEGORIES = [
  { value: 'paginas-web', label: 'Páginas Web' },
  { value: 'sistemas', label: 'Sistemas' },
  { value: 'crm', label: 'CRM' },
  { value: 'punto-de-venta', label: 'Punto de Venta' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'mantenimiento', label: 'Mantenimiento' }
];

const toLines = (value) => (Array.isArray(value) ? value.join('\n') : '');
const fromLines = (value) => String(value || '').split('\n').map((s) => s.trim()).filter(Boolean);
const faqToLines = (items) => Array.isArray(items) ? items.map((item) => `${item.question || ''} | ${item.answer || ''}`).filter(Boolean).join('\n') : '';
const faqFromLines = (value) => String(value || '').split('\n').map((line) => {
  const [question, ...rest] = line.split('|');
  return { question: question.trim(), answer: rest.join('|').trim() };
}).filter((item) => item.question && item.answer).slice(0, 20);

// Genera un slug a partir del nombre. Es un identificador estable que usan
// las landings públicas y el backend; si el usuario no lo escribe, se deriva.
const slugify = (value) => String(value || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function CatalogoProductos({ onCatalogChange }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.listCatalog();
      setServices(result.services || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = services.filter((service) => showInactive || service.active);

  const openNew = () => setEditing({ ...EMPTY_PRODUCT });
  const openEdit = (service) => setEditing({
    ...service,
    base_price: service.base_price ?? '',
    image_url: service.image_url || '',
    benefits: toLines(service.benefits),
    deliverables: toLines(service.deliverables),
    combos: toLines(service.combos),
    promotional_price: service.promotional_price ?? '',
    promotion_label: service.promotion_label || '',
    landing_url: service.landing_url || '',
    seo_title: service.seo?.title || '',
    seo_description: service.seo?.description || '',
    faq: faqToLines(service.faq)
  });

  const save = async () => {
    const payload = {
      name: editing.name.trim(),
      slug: (editing.slug || slugify(editing.name)).trim(),
      category: editing.category,
      description: editing.description.trim(),
      base_price: Number(editing.base_price || 0),
      billing_type: editing.billing_type,
      image_url: editing.image_url.trim() || null,
      sort_order: Number(editing.sort_order) || 100,
      featured: !!editing.featured,
      active: !!editing.active,
      benefits: fromLines(editing.benefits),
      deliverables: fromLines(editing.deliverables),
      combos: fromLines(editing.combos),
      promotional_price: editing.promotional_price === '' ? null : Number(editing.promotional_price),
      promotion_label: editing.promotion_label.trim() || null,
      landing_url: editing.landing_url.trim() || null,
      seo: { title: editing.seo_title.trim(), description: editing.seo_description.trim() },
      faq: faqFromLines(editing.faq)
    };
    if (!payload.name || !payload.slug) {
      setNotice({ tone: 'warning', text: 'El producto necesita un nombre.' });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await adminService.updateProduct(editing.id, payload);
      else await adminService.createProduct(payload);
      setNotice({ tone: 'success', text: editing.id ? 'Producto actualizado.' : 'Producto agregado al catálogo.' });
      setEditing(null);
      await load();
      onCatalogChange?.();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (service) => {
    setSaving(true);
    try {
      await adminService.updateProduct(service.id, { active: !service.active });
      setNotice({ tone: 'success', text: service.active ? 'Producto archivado.' : 'Producto reactivado.' });
      setArchiving(null);
      await load();
      onCatalogChange?.();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
      setArchiving(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        icon={<Package className="h-5 w-5" />}
        title="Catálogo de productos"
        subtitle="Lo que MARKETOS vende. Agregar un producto aquí lo hace asignable a cualquier cliente."
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowInactive((v) => !v)}>
              {showInactive ? 'Ocultar archivados' : 'Ver archivados'}
            </Button>
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={openNew}>Nuevo producto</Button>
          </>
        }
      />

      <div className="p-4 sm:p-5">
        {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

        <AsyncBoundary
          loading={loading}
          error={error}
          onRetry={load}
          empty={!visible.length}
          emptyProps={{
            title: 'El catálogo está vacío',
            message: 'Agrega tu primer producto para poder asignarlo a un cliente.',
            action: <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={openNew}>Nuevo producto</Button>
          }}
          loadingLabel="Cargando catálogo…"
        >
          <div className={`grid gap-4 sm:grid-cols-2 xl:grid-cols-3 ${notice ? 'mt-4' : ''}`}>
            {visible.map((service) => (
              <article
                key={service.id}
                className={`flex flex-col overflow-hidden rounded-2xl border transition hover:shadow-soft
                  ${service.featured ? 'border-gold' : 'border-navy-50'} ${service.active ? 'bg-white' : 'bg-navy-50/40 opacity-70'}`}
              >
                <div className="relative h-28 bg-navy-50">
                  {service.image_url
                    ? <img src={service.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                    : <div className="grid h-full place-items-center text-navy-900/25"><ImageIcon className="h-8 w-8" /></div>}
                  {service.featured && (
                    <span className="absolute left-2 top-2">
                      <Badge tone="gold"><Star className="h-3 w-3" /> Destacado</Badge>
                    </span>
                  )}
                  {!service.active && <span className="absolute right-2 top-2"><Badge tone="gray">Archivado</Badge></span>}
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-bold text-navy-900">{service.name}</h4>
                    <p className="shrink-0 font-extrabold text-magenta">{formatMoney(service.base_price)}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-navy-900/50">
                    {CATEGORIES.find((c) => c.value === service.category)?.label || service.category}
                    {' · '}
                    {BILLING_TYPES.find((b) => b.value === service.billing_type)?.label || service.billing_type}
                  </p>
                  <p className="mt-2 line-clamp-3 flex-1 text-sm text-navy-900/70">{service.description}</p>

                  {service.benefits?.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {service.benefits.slice(0, 3).map((benefit, index) => (
                        <li key={index} className="flex gap-1.5 text-xs text-navy-900/60">
                          <span className="text-magenta" aria-hidden="true">•</span>{benefit}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button variant="soft" size="sm" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => openEdit(service)} className="flex-1">
                      Editar
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      icon={service.active ? <Archive className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      onClick={() => setArchiving(service)}
                      title={service.active ? 'Archivar' : 'Reactivar'}
                    >
                      {service.active ? 'Archivar' : 'Reactivar'}
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </AsyncBoundary>
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        size="lg"
        title={editing?.id ? 'Editar producto' : 'Nuevo producto'}
        subtitle="Lo que escribas aquí es lo que verá el cliente en su portal."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={save} loading={saving}>{editing?.id ? 'Guardar cambios' : 'Crear producto'}</Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nombre" required>
                <Input
                  value={editing.name}
                  onChange={(event) => setEditing({
                    ...editing,
                    name: event.target.value,
                    // El slug solo se autogenera para productos nuevos: cambiarlo
                    // en uno existente rompería referencias ya publicadas.
                    slug: editing.id ? editing.slug : slugify(event.target.value)
                  })}
                  placeholder="Ej.: Landing Comercial"
                />
              </Field>
              <Field label="Identificador" hint="Se usa en enlaces. No se puede repetir.">
                <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} disabled={!!editing.id} />
              </Field>
              <Field label="Categoría">
                <Select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </Field>
              <Field label="Tipo de cobro">
                <Select value={editing.billing_type} onChange={(e) => setEditing({ ...editing, billing_type: e.target.value })}>
                  {BILLING_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </Select>
              </Field>
              <Field label="Precio" hint="Cambiar un precio publicado es una decisión comercial: regístrala en la bitácora.">
                <Input type="number" min="0" step="1" value={editing.base_price} onChange={(e) => setEditing({ ...editing, base_price: e.target.value })} />
              </Field>
              <Field label="Orden de aparición" hint="Menor número aparece primero.">
                <Input type="number" min="1" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </Field>
            </div>

            <Field label="Descripción comercial" hint="Sin tecnicismos: qué gana el cliente.">
              <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field>

            <Field label="Imagen" hint="Enlace a la imagen que representa el producto.">
              <Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://…" />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Precio promocional" hint="Opcional. Se muestra junto al precio base.">
                <Input type="number" min="0" step="1" value={editing.promotional_price} onChange={(e) => setEditing({ ...editing, promotional_price: e.target.value })} placeholder="Ej.: 99" />
              </Field>
              <Field label="Mensaje de promoción" hint="Ej.: Lanzamiento por tiempo limitado.">
                <Input value={editing.promotion_label} onChange={(e) => setEditing({ ...editing, promotion_label: e.target.value })} />
              </Field>
            </div>

            <Field label="Landing asociada" hint="Enlace público de la página que vende este producto.">
              <Input value={editing.landing_url} onChange={(e) => setEditing({ ...editing, landing_url: e.target.value })} placeholder="https://marketos.click/paginas-web" />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Título SEO" hint="Título que verá Google y al compartir el enlace.">
                <Input value={editing.seo_title} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} />
              </Field>
              <Field label="Descripción SEO" hint="Resumen comercial breve para buscadores.">
                <Textarea rows={2} value={editing.seo_description} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Beneficios" hint="Uno por línea.">
                <Textarea rows={4} value={editing.benefits} onChange={(e) => setEditing({ ...editing, benefits: e.target.value })} placeholder={'Diseño profesional\nBotón directo a WhatsApp'} />
              </Field>
              <Field label="Entregables" hint="Uno por línea. Qué recibe exactamente.">
                <Textarea rows={4} value={editing.deliverables} onChange={(e) => setEditing({ ...editing, deliverables: e.target.value })} placeholder={'Página publicada\nCapacitación de 30 min'} />
              </Field>
            </div>

            <Field label="Combos relacionados" hint="Identificadores de otros productos, uno por línea.">
              <Textarea rows={2} value={editing.combos} onChange={(e) => setEditing({ ...editing, combos: e.target.value })} placeholder="landing-captacion" />
            </Field>

            <Field label="Preguntas frecuentes" hint="Una por línea con el formato: Pregunta | Respuesta.">
              <Textarea rows={4} value={editing.faq} onChange={(e) => setEditing({ ...editing, faq: e.target.value })} placeholder={'¿Incluye dominio? | Te ayudamos a conectarlo.\n¿En cuánto tiempo estará lista? | Coordinamos la entrega contigo.'} />
            </Field>

            <div className="flex flex-wrap gap-5 rounded-xl bg-cream p-4">
              <Toggle label="Producto destacado" checked={!!editing.featured} onChange={(featured) => setEditing({ ...editing, featured })} />
              <Toggle label="Activo" checked={!!editing.active} onChange={(active) => setEditing({ ...editing, active })} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!archiving}
        loading={saving}
        title={archiving?.active ? 'Archivar producto' : 'Reactivar producto'}
        confirmLabel={archiving?.active ? 'Archivar' : 'Reactivar'}
        tone={archiving?.active ? 'danger' : 'primary'}
        onCancel={() => setArchiving(null)}
        onConfirm={() => toggleActive(archiving)}
        message={archiving?.active
          ? 'Dejará de poder asignarse a clientes nuevos. Los clientes que ya lo tienen contratado lo seguirán viendo con normalidad.'
          : 'Volverá a estar disponible para asignarlo a clientes.'}
      />
    </Card>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-navy-900">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-navy-100 text-magenta focus:ring-magenta"
      />
      {label}
    </label>
  );
}
